import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCan } from '@/auth/consoleCapabilityContext';
import { AccountsPage } from '../AccountsPage';
import { canListAccounts } from '../Accounts/accountsAccess';
import { AppDetailAccountsTab } from './AppDetailAccountsTab';
import type { AppDetailPageVM } from './useAppDetailPage';
import styles from './AppDetailAccountsSection.module.css';

export type AppDetailAccountsView = 'accounts' | 'sync';

/** `?view=sync` selects the provisioning view; anything else (or nothing) is
 *  the accounts board, so `?tab=accounts` deep links land on the default. */
function parseAccountsView(raw: string | null): AppDetailAccountsView {
    return raw === 'sync' ? 'sync' : 'accounts';
}

export interface AppDetailAccountsSectionProps {
    vm: AppDetailPageVM;
    /** Forwarded to the "Đồng bộ" view unchanged — see `AppDetailAccountsTab`. */
    onSyncUsers?: () => void;
}

/**
 * EAA-2068: `/console/apps/:code?tab=accounts`. Two sub-views:
 *
 *  - "Tài khoản" (default, `view` absent) — the SAME board as `/console/accounts`
 *    (`AccountsPage` with `lockedApp`), so every row action, bulk action
 *    (including the cross-page bulk password reset), select-all-matching, the
 *    detail modal, lock, delete and assign-Level work here exactly as there,
 *    gated by the same `console.accounts.*` keys.
 *  - "Đồng bộ" (`view=sync`) — the provisioning view this tab used to be
 *    (outcome per assignment, retry, pull users from the app), unchanged.
 *
 * A viewer with none of the account-list keys (`canListAccounts`) gets only the
 * "Đồng bộ" view and no sub-view strip, i.e. exactly the tab as it was before.
 */
export function AppDetailAccountsSection({ vm, onSyncUsers }: AppDetailAccountsSectionProps) {
    const { t } = useTranslation();
    const can = useCan();
    const [searchParams, setSearchParams] = useSearchParams();

    const setView = useCallback(
        (next: AppDetailAccountsView) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'accounts') params.delete('view');
                else params.set('view', next);
                return params;
            });
        },
        [setSearchParams]
    );

    const app = vm.app;
    if (!app) return null;

    const syncView = <AppDetailAccountsTab vm={vm} onSyncUsers={onSyncUsers} />;

    if (!canListAccounts(can)) {
        return syncView;
    }

    const view = parseAccountsView(searchParams.get('view'));

    function tabButton(key: AppDetailAccountsView, label: string) {
        return (
            <button
                key={key}
                type="button"
                role="tab"
                id={`app-accounts-view-${key}`}
                aria-selected={view === key}
                aria-controls={`app-accounts-panel-${key}`}
                className={'acc-tab' + (view === key ? ' acc-tab--active' : '')}
                onClick={() => setView(key)}
            >
                {label}
            </button>
        );
    }

    return (
        <div className={`portal-console-page--fit ${styles.section}`}>
            <div className="acc-tabs" role="tablist" aria-label={t('console.appDetail.accounts.views.ariaLabel')}>
                {tabButton('accounts', t('console.appDetail.accounts.views.accounts'))}
                {tabButton('sync', t('console.appDetail.accounts.views.sync'))}
            </div>
            <div
                className="fit-grow"
                role="tabpanel"
                id={`app-accounts-panel-${view}`}
                aria-labelledby={`app-accounts-view-${view}`}
            >
                {view === 'accounts' ? (
                    <AccountsPage key={app.code} lockedApp={{ code: app.code, name: app.name }} />
                ) : (
                    syncView
                )}
            </div>
        </div>
    );
}

export default AppDetailAccountsSection;
